<script setup>

import { computed, nextTick, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useSessionStore, useAuthStore, useAlertStore } from '@lib/pp-shop-lib/stores';
import StepProgress from 'vue-step-progress';
import { Cart, OrderAddresses, UserEmail, Shipping, Payment, Confirm, Success, Error } from '@lib/pp-shop-lib/components/checkout';
import { Alert } from '@lib/pp-shop-lib/components';
import { useI18n } from 'vue-i18n';


const sessionStore = useSessionStore();
const authStore = useAuthStore();
const alertStore = useAlertStore();
const { checkout } = storeToRefs(sessionStore);
const { user } = storeToRefs(authStore);
const { t } = useI18n({ useScope: 'global' });

const steps = computed(() => [
    t('checkout.steps.cart'),
    t('checkout.steps.delivery'),
    t('checkout.steps.payment'),
    t('checkout.steps.confirm'),
    t('checkout.steps.success')
])
const stepNumber = {
    cart: 0, delivery: 1, payment: 2, confirm: 3, success: 5
}
const currentStep = ref(stepNumber[checkout.value.section])
const addrIsValid = ref(false)
const emailIsValid = ref(!!user.value?.token)
const activationSentTo = ref('')
const deliveryStepIsValid = computed(() => emailIsValid.value && addrIsValid.value)

watch(() => checkout.value.section, async (newSection, oldSection) => {
    if (newSection === oldSection) return

    if (Object.prototype.hasOwnProperty.call(stepNumber, newSection)) {
        currentStep.value = stepNumber[newSection]
    }

    await nextTick()
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
})

function section(name, data = {}) {
    if (name === 'confirm' || name === 'payment') {
        alertStore.clear('checkout')
    }
    if (name === 'success') {
        activationSentTo.value = String(data?.activationSentTo || '').trim()
    } else if (name === 'cart') {
        activationSentTo.value = ''
    }
    checkout.value.section = name;
}

async function backToOrderSummary() {
    alertStore.clear('checkout')

    if (checkout.value.section === 'confirm') {
        checkout.value.section = 'cart'
        await nextTick()
    }

    checkout.value.section = 'confirm'
}

window.section = section;

</script>

<template>
    <div class="step-progress">
        <step-progress :steps="steps" :current-step="currentStep" icon-class="step-check" active-color="#1e87f0"
            active-thickness=1 passive-thickness=1 line-thickness=1></step-progress>
    </div>
    <Alert scope="checkout" />
    <div v-if="alertStore.alertsByScope.checkout" class="checkout-error-actions">
        <a href="#" @click.prevent="backToOrderSummary()"
            class="checkout-error-link">{{ $t('checkout.common.backToOrderSummary') }}</a>
        <button @click="section('payment')"
            class="cart-button uk-button uk-button-default uk-button-small uk-button-primary">
            {{ $t('checkout.common.chooseOtherPayment') }}
        </button>
    </div>
    <div v-if="checkout.section == 'cart'">
        <Cart @section="section"></Cart>
    </div>
    <div v-if="checkout.section == 'delivery'">
        <div class="delivery-sections">
            <UserEmail @is-valid="(d) => emailIsValid = d"></UserEmail>
            <OrderAddresses @is-valid="(d) => addrIsValid = d" @section="section"></OrderAddresses>
            <Shipping :addressIsValid="addrIsValid && (user?.token || emailIsValid)" @section="section"></Shipping>
        </div>
        <div class="delivery-nav-row">
            <button @click="section('cart')"
                class="cart-button uk-button uk-button-default uk-button-small">
                {{ $t('checkout.common.backToCart') }}
            </button>
            <button @click="section('payment')"
                :disabled="!deliveryStepIsValid"
                class="cart-button uk-button uk-button-default uk-button-small uk-button-primary">
                {{ $t('checkout.common.continueToPayment') }}
            </button>
        </div>
    </div>
    <div v-if="checkout.section == 'payment'">
        <Payment @section="section"></Payment>
    </div>
    <div v-if="checkout.section == 'confirm'">
        <Confirm @section="section"></Confirm>
    </div>
    <div v-if="checkout.section == 'success'">
        <Success :activation-sent-to="activationSentTo" @section="section"></Success>
    </div>
    <div v-if="checkout.section == 'error'">
        <Error @section="section"></Error>
    </div>
</template>

<style lang="scss">
[class*="checkout-"].uk-card {
  box-shadow: none;
  border: none;
}

table.cart tfoot td:last-child {
    font-weight: bold;
}

.step-progress {
    margin-left: 10px;
    margin-right: 10px;
    margin-bottom: 40px;
}

.delivery-nav-row {
    width: 100%;
    margin-top: 0px;
    margin-bottom: 5px;
    
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
}

.delivery-sections {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    align-items: start;
    gap: 16px;
    max-width: 1200px;
    margin-inline: auto;
}

.delivery-sections > .checkout-user-email,
.delivery-sections > .checkout-addresses,
.delivery-sections > .checkout-shipping {
    width: 100%;
    min-width: 0;
    margin: 0;
}

.delivery-sections h3 {
    overflow-wrap: anywhere;
}

.checkout-error-actions {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    margin: 8px 0 16px;
}

@media (max-width: 640px) {
    .delivery-nav-row {
        width: auto;
       
    }
}

@media (max-width: 900px) {
    .delivery-sections {
        grid-template-columns: minmax(0, 1fr);
    }

    .delivery-sections > .checkout-user-email,
    .delivery-sections > .checkout-addresses,
    .delivery-sections > .checkout-shipping {
        max-width: 560px;
        margin-inline: auto;
    }
}

@media (max-width: 768px) {
    .step-progress__step .step-progress__step-label {
        width: 20px;
        overflow: hidden;
    }
}

@media (max-width: 768px) {
    .step-progress__step .step-progress__step-label:before {
        content: "";
        background: url(#{$imagePath}/cart-shopping-solid.svg);
        background-repeat: no-repeat;
        display: block;
        width: 20px;
        height: 20px;
        float: left;
        margin: 0 6px 0 0;
        opacity: 0.4;
    }

    .step-progress__step .step-progress__step-label:hover {
        overflow: visible;
        opacity: 1;
        white-space: initial;
    }

    step-progress__step:nth-child(1) .step-progress__step-label:before {
        background: url(#{$imagePath}/truck-solid.svg);
        background-repeat: no-repeat;
    }

    .step-progress__step:nth-child(2) .step-progress__step-label:before {
        background: url(#{$imagePath}/truck-solid.svg);
        background-repeat: no-repeat;
    }

    .step-progress__step:nth-child(3) .step-progress__step-label:before {
        background: url(#{$imagePath}/money-bill-1-solid.svg);
        background-repeat: no-repeat;
    }

    .step-progress__step:nth-child(4) .step-progress__step-label:before {
        background: url(#{$imagePath}/list-check-solid.svg);
        background-repeat: no-repeat;
    }

    .step-progress__step:nth-child(5) .step-progress__step-label:before {
        background: url(#{$imagePath}/clipboard-check-solid.svg);
        background-repeat: no-repeat;
    }
}
.checkout-user-email {
    padding-bottom: 0px;
}
.checkout-user-email, .checkout-addresses, .checkout-shipping {
    margin-bottom: 0px;
    margin-top: 0px;
}
</style>